import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import Loader from '../layouts/Loader';
import MetaData from "../layouts/MetaData";
import { toast } from "react-toastify";
import { getpatient } from "../../actions/patientActions";
import { clearError, clearPatient } from "../../slices/patientSlice";

// Components
import InPatient from "./InPatient";
import OutPatient from "./OutPatient";
import PatientDietSection from "./PatientDietSection";
import PatientBilling from "./PatientBilling";

export default function PatientDetail() {
    const { loading, patient = {}, error } = useSelector((state) => state.patientState);
    const { bills = [] } = useSelector((state) => state.billingState);
    const dispatch = useDispatch();
    const { id } = useParams();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('all'); // 'all', 'visits', 'billing', 'diet'

    useEffect(() => {
        if (id) dispatch(getpatient(id));
        return () => {
            dispatch(clearPatient());
        };
    }, [dispatch, id]);

    useEffect(() => {
        if (error) {
            toast(error, { type: 'error' });
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const liveBalanceDue = (() => {
        if (bills && bills.length > 0) {
            return bills.reduce((acc, b) => b.status !== 'Cancelled' ? acc + Number(b.balanceDue || 0) : acc, 0);
        }
        return Number(patient.balanceDue || patient.totalDue || 0);
    })();

    // Calculate unique visit dates count (visits are based on distinct dates, not individual treatments)
    const uniqueVisitCount = (() => {
        if (!patient.visits || !Array.isArray(patient.visits)) return 0;
        const dates = new Set();
        patient.visits.forEach(v => {
            if (v.date) {
                try {
                    dates.add(new Date(v.date).toISOString().split('T')[0]);
                } catch (e) {
                    dates.add(String(v.date).trim());
                }
            }
        });
        return dates.size;
    })();

    return (
        <Fragment>
            {loading ? <Loader /> : (
                <div className="container-fluid p-4">
                    <MetaData title={patient.name} />

                    {/* Top Header & Navigation Bar */}
                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                        <div className="d-flex align-items-center flex-wrap gap-2">
                            <button 
                                className="btn btn-outline-secondary btn-sm me-3" 
                                onClick={() => navigate(-1)}
                            >
                                <i className="fa fa-arrow-left me-1"></i> Back
                            </button>
                            <h4 className="mb-0 fw-bold">{patient.name || 'Patient'}</h4>
                            {patient.patientId && (
                                <span className="badge bg-primary ms-2">{patient.patientId}</span>
                            )}
                            {patient.category && (
                                <span className="badge bg-secondary ms-1">{patient.category}</span>
                            )}
                        </div>

                        {/* Section Switcher Pills */}
                        <div className="btn-group btn-group-sm flex-wrap" role="group">
                            <button 
                                type="button" 
                                className={`btn ${activeTab === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setActiveTab('all')}
                            >
                                <i className="fa fa-th-large me-1"></i> All
                            </button>
                            <button 
                                type="button" 
                                className={`btn ${activeTab === 'visits' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setActiveTab('visits')}
                            >
                                <i className="fa fa-stethoscope me-1"></i> Visits
                                {uniqueVisitCount > 0 && (
                                    <span className={`badge ${activeTab === 'visits' ? 'bg-white text-primary' : 'bg-primary text-white'} ms-1`}>
                                        {uniqueVisitCount}
                                    </span>
                                )}
                            </button>
                            <button 
                                type="button" 
                                className={`btn ${activeTab === 'billing' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setActiveTab('billing')}
                            >
                                <i className="fa fa-file-invoice-dollar me-1"></i> Billing
                                {liveBalanceDue > 0 ? (
                                    <span className="badge bg-danger ms-1">₹{liveBalanceDue.toLocaleString('en-IN')}</span>
                                ) : (
                                    bills && bills.length > 0 && (
                                        <span className="badge bg-success ms-1">Paid</span>
                                    )
                                )}
                            </button>
                            <button 
                                type="button" 
                                className={`btn ${activeTab === 'diet' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setActiveTab('diet')}
                            >
                                <i className="fa fa-apple-alt me-1"></i> Diet
                            </button>
                        </div>
                    </div>

                    {/* Visits / Medical Section */}
                    {(activeTab === 'all' || activeTab === 'visits') && (
                        <div className="mb-4">
                            {patient.category === "In Patient" ? (
                                <InPatient patient={patient} />
                            ) : (
                                <OutPatient patient={patient} />
                            )}
                        </div>
                    )}

                    {/* Billing Section (placed right after OutPatient, before Diet) */}
                    {(activeTab === 'all' || activeTab === 'billing') && (patient._id || patient.id) && (
                        <div className="mb-4">
                            <PatientBilling patient={patient} />
                        </div>
                    )}

                    {/* Diet Section */}
                    {(activeTab === 'all' || activeTab === 'diet') && (patient._id || patient.id) && (
                        <div className="mb-4">
                            <PatientDietSection patient={patient} />
                        </div>
                    )}
                </div>
            )}
        </Fragment>
    );
}
